/**
 * Flatten express-validator style errors (including nestedErrors) into display lines.
 * @param {unknown} entry
 * @returns {string[]}
 */
function flattenValidatorEntry(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const lines = [];
  const field = entry.path ?? entry.param;
  const text = entry.msg ?? entry.message;
  if (text && typeof text === 'string') {
    lines.push(field ? `${field}: ${text}` : text);
  }
  if (Array.isArray(entry.nestedErrors)) {
    for (const n of entry.nestedErrors) {
      lines.push(...flattenValidatorEntry(n));
    }
  }
  return lines;
}

/**
 * @param {unknown} errors — array from express-validator or similar
 * @returns {string}
 */
function formatErrorsArray(errors) {
  if (!Array.isArray(errors) || errors.length === 0) return '';
  const lines = errors.flatMap((e) => flattenValidatorEntry(e)).filter(Boolean);
  return lines.length ? lines.join('\n• ') : '';
}

/**
 * @param {unknown} errors — object map field -> message | string[]
 * @returns {string}
 */
function formatErrorsObject(errors) {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return '';
  const lines = Object.entries(errors).flatMap(([key, val]) => {
    if (Array.isArray(val)) {
      return val.map((v) => (typeof v === 'string' ? `${key}: ${v}` : `${key}: ${JSON.stringify(v)}`));
    }
    if (typeof val === 'string') return [`${key}: ${val}`];
    return [`${key}: ${JSON.stringify(val)}`];
  });
  return lines.length ? lines.join('\n• ') : '';
}

/**
 * Build a single user-visible message from API JSON error bodies.
 * @param {object|null|undefined} responseData
 * @param {number} httpStatus
 */
export function buildApiErrorMessage(responseData, httpStatus) {
  const base =
    (responseData && (responseData.message || responseData.error)) ||
    `Request failed (${httpStatus})`;

  let details = '';
  const raw = responseData?.errors;
  if (Array.isArray(raw)) {
    details = formatErrorsArray(raw);
  } else if (raw && typeof raw === 'object') {
    details = formatErrorsObject(raw);
  }

  if (!details) return base;
  return `${base}\n• ${details}`;
}

/**
 * Message for catch blocks after makeAuthenticatedRequest (throws Error with .message set).
 * @param {unknown} error
 * @param {string} fallback
 */
export function getCustomerPortalErrorMessage(error, fallback = 'Something went wrong') {
  if (error && typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return fallback;
}
